import React, {useEffect, useState} from 'react'
import Modal from '../../Modal'
import {ComplianceDecisionRequestModel, ComplianceStatus,} from '../../../redux/companyMonitorTypes'
import {msalInstance} from '../../../services/msalService'
import Form from 'react-bootstrap/Form'
import {Colors} from "../../../constants/colors"
import MDEditor from "@uiw/react-md-editor"
import "@uiw/react-md-editor/markdown-editor.css"
import "@uiw/react-markdown-preview/markdown.css"
import {createUseStyles} from "react-jss"

interface Props {
  visible: boolean
  onCloseClick?: () => void
  onComplianceDecisionCreated: (payload: ComplianceDecisionRequestModel) => void
}

type DecisionDraft = {
  status: ComplianceStatus
  decidedBy: string | null
  reason: string | null
  investigationReference: string | null
}

const defaultDraft: DecisionDraft = {
  status: ComplianceStatus.unknown,
  decidedBy: null,
  reason: null,
  investigationReference: null,
}

type ValidationState = {
  isValid: boolean
  statusValid: boolean
  statusError?: string
  reasonValid: boolean
  reasonError?: string
}

const defaultValidationState: ValidationState = {
  isValid: true,
  statusValid: true,
  statusError: undefined,
  reasonValid: true,
  reasonError: undefined,
}

const ComplianceDecisionModal = ({
                                   visible,
                                   onCloseClick,
                                   onComplianceDecisionCreated,
                                 }: Props) => {
  const styles = useStyles()
  const [draft, setDraft] = useState<DecisionDraft>(defaultDraft)
  const [validation, setValidation] = useState<ValidationState>(defaultValidationState)

  useEffect(() => {
    if (visible) {
      const account = msalInstance.getActiveAccount()
      const decidedBy = `${account?.name} (${account?.username})`

      setDraft({
        ...defaultDraft,
        decidedBy,
      })
      setValidation(defaultValidationState)
    }
  }, [visible])

  const validate = (): boolean => {
    const trimmedReason = draft.reason?.trim() ?? ''

    const statusValid = draft.status !== ComplianceStatus.unknown
    const statusError = statusValid ? undefined : 'Please select a compliance status'

    let reasonValid = true
    let reasonError: string | undefined

    if (!trimmedReason) {
      reasonValid = false
      reasonError = 'Reason is required'
    }
    else if (trimmedReason.length < 5) {
      reasonValid = false
      reasonError = 'Reason must be at least 5 characters'
    }

    const isValid = statusValid && reasonValid

    setValidation({
      isValid,
      statusValid,
      statusError,
      reasonValid,
      reasonError,
    })

    return isValid
  }

  const handleOnClose = () => {
    onCloseClick && onCloseClick()
  }

  const handleOnOk = () => {
    if (!validate()) {
      return
    }

    const payload: ComplianceDecisionRequestModel = {
      status: draft.status,
      decidedBy: draft.decidedBy,
      reason: draft.reason ?? '',
      investigationReference: draft.investigationReference ?? '',
    }

    onComplianceDecisionCreated(payload)
  }

  const handleStatusChange = (value: string) => {
    setDraft((prev) => ({
      ...prev,
      status: value as ComplianceStatus,
    }))
    // Clear validation when user changes selection
    setValidation(defaultValidationState)
  }

  const handleReasonChange = (value?: string) => {
    setDraft((prev) => ({...prev, reason: value ?? ''}))
    // Clear validation when user edits the reason
    setValidation(defaultValidationState)
  }

  const handleInvestigationReferenceChange = (value?: string) => {
    setDraft((prev) => ({...prev, investigationReference: value ?? ''}))
  }

  return (
    <Modal
      title={'Compliance Decision'}
      show={visible}
      okButtonTitle='Submit decision'
      cancelButtonTitle='Cancel'
      okButtonDisabled={!validation.isValid}
      onHide={handleOnClose}
      onOkClick={handleOnOk}
      onCancelClick={handleOnClose}
      size='xl'
    >
      <Form noValidate>
        <Form.Group className="mb-3" controlId="complianceStatus">
          <Form.Label>
            Compliance status{' '}
            <span style={{ color: Colors.rubel_500 }}>*</span>
          </Form.Label>
          <Form.Select
            value={draft.status}
            onChange={(event) => handleStatusChange(event.target.value)}
            isInvalid={!validation.statusValid}
          >
            <option value={ComplianceStatus.unknown} disabled>
              Select status…
            </option>
            <option value={ComplianceStatus.compliant}>Compliant</option>
            <option value={ComplianceStatus.nonCompliant}>Non-compliant</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {validation.statusError}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="reason">
          <Form.Label>
            Reason{' '}
            <span style={{ color: Colors.rubel_500 }}>*</span>
          </Form.Label>
          <div
            data-color-mode="dark"
            className={`${styles.editorWrapper} ${!validation.reasonValid ? styles.invalidEditorWrapper : ''}`}
          >
            <MDEditor
              id="reason"
              preview="edit"
              height={160}
              value={draft.reason ?? ''}
              onChange={handleReasonChange}
              textareaProps={{
                placeholder: 'Short description of the reason behind this decision',
              }}
            />
          </div>
          <Form.Control.Feedback type="invalid" style={{display: !validation.reasonValid ? 'block' : undefined}}>
            {validation.reasonError}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-0" controlId="investigationReference">
          <Form.Label>Investigation reference</Form.Label>
          <div data-color-mode="dark" className={styles.editorWrapper}>
            <MDEditor
              id="investigationReference"
              preview="edit"
              height={160}
              value={draft.investigationReference ?? ''}
              onChange={handleInvestigationReferenceChange}
              textareaProps={{
                placeholder: 'External documentation or reports related to this decision (optional)',
              }}
            />
          </div>
        </Form.Group>

        <Form.Text id="decisionLoggedByHelp"
                   style={{
                     display: 'block',
                     marginTop: 20,
                     color: Colors.darkGray2,
                   }}>
          {draft.decidedBy
            ? `This decision is made by: ${draft.decidedBy}.`
            : 'This decision will be marked with your current user account.'}
        </Form.Text>
      </Form>
    </Modal>
  )
}

export default ComplianceDecisionModal

const useStyles = createUseStyles({
  editorWrapper: {
    '& .w-md-editor': {
      backgroundColor: Colors.grayCard,
    },
    '& .w-md-editor-toolbar': {
      backgroundColor: Colors.grayCard,
      borderBottomColor: Colors.grayBorder,
    },
    '& .w-md-editor-text-input, & .w-md-editor-text-pre > code, & .w-md-editor-text-pre': {
      fontFamily: '"Aestetico-Regular", sans-serif',
      fontSize: 14,
      lineHeight: '1.6rem',
    },
  },
  invalidEditorWrapper: {
    '& .w-md-editor': {
      borderColor: Colors.rubel_500,
    },
  },
})
